import React from "react";
import { useSelector } from "react-redux";

import { Box, IconButton, Select, MenuItem, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { GridPaginationModel } from "@mui/x-data-grid";

import { RootState } from "../../store/Index";

export interface PaginationProps {
  page: number;
  pageSize: number;
}

interface PaginatorProps {
  count: number;
  pagination: GridPaginationModel;
  setPagination: (pagination: GridPaginationModel) => void;
}

export const Paginator: React.FC<PaginatorProps> = ({
  count,
  pagination,
  setPagination,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const getPaginationProgress = () => {
    if (pagination && count) {
      const floor =
        (pagination.page + 1) * pagination.pageSize - pagination.pageSize + 1;
      const ceiling = (pagination.page + 1) * pagination.pageSize;

      return (
        <Typography variant={"body2"}>
          {`${floor} - ${ceiling > count ? count : ceiling}`}
        </Typography>
      );
    }
  };

  if (!pagination || !count) return null;

  return (
    <Box
      display={"flex"}
      alignItems={"center"}
      justifyContent={"flex-end"}
      mt={1}
      gap={1}
    >
      <Box display={"flex"} alignItems={"center"} gap={0.2}>
        <Typography variant={"body2"}>Rows:</Typography>
        <Select
          value={pagination.pageSize || 10}
          onChange={(e) =>
            setPagination({
              page: 0,
              pageSize: Number(e.target.value),
            })
          }
          size="small"
          sx={{
            color:
              reduxTheme.primary_colors[100] || reduxTheme.primary_colors[400],
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: reduxTheme.primary_colors[800] || "divider",
            },
            ".MuiSvgIcon-root": {
              color:
                reduxTheme.primary_colors[100] ||
                reduxTheme.primary_colors[400],
            },
          }}
        >
          {[5, 10, 15, 20].map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box display={"flex"} alignItems={"center"} gap={0.8}>
        <Box display={"flex"} alignItems={"center"} gap={0.4}>
          {getPaginationProgress()}
          <Typography variant={"body2"}>{`of ${count}`}</Typography>
        </Box>
        <Box display={"flex"} alignItems={"center"} gap={1}>
          <IconButton
            sx={{ size: "xs", backgroundColor: "#FFF" }}
            disabled={pagination.page === 0}
            onClick={() =>
              setPagination({
                ...pagination,
                page: pagination.page - 1,
              })
            }
          >
            <ChevronLeft sx={{ size: 18 }} />
          </IconButton>

          <IconButton
            sx={{ size: "xs", backgroundColor: "#FFF" }}
            disabled={count < (pagination.page + 1) * pagination.pageSize}
            onClick={() =>
              setPagination({
                ...pagination,
                page: pagination.page + 1,
              })
            }
          >
            <ChevronRight sx={{ size: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default Paginator;
